/**
 * Model-authored prose, resolved to a string — or refused.
 *
 * generated-text.schema.json `x-validation` is the Server's contract and runs
 * before storage. Five of its gates are re-run HERE, on the way to the screen,
 * because the failure they catch is a rendering failure: a template that got a
 * currency symbol or an unresolved {slot} past the Server would put a broken
 * sentence in front of the user, while the copyKey sitting next to it is always
 * correct and always translated. Cheap to check, and the fallback is free.
 *
 * The two gates NOT re-run here are the two a client cannot evaluate:
 * `paramsProvenance` needs the deterministic figures the Server computed, and
 * `noSecondPersonClaims` is a semantic judgement. Both stay Server-side — see
 * src/lib/mock/generation.ts for the stub Server's implementation of them.
 *
 * Every gate fails the same way: `null`, which the shared <GeneratedText/>
 * component renders as the copyKey. There is no state in which a slot is blank
 * and no state in which one shows an error.
 */
import type { GeneratedText, Money } from '@/generated/types'
import { formatDate, formatMonth, formatMoney, formatNumber, formatPercent } from './format'

/** Which gate refused, for logs and for the gate tests. `null` means it passed. */
export type GateFailure =
  | 'lengthCaps'
  | 'noMarkup'
  | 'noFormattedNumbers'
  | 'placeholdersResolve'
  | 'localeMatch'

const MAX_TEMPLATE = 400
const MAX_BULLET = 160
const MAX_BULLETS = 6
const MAX_PARAMS = 8

/**
 * x-validation noFormattedNumbers. The Server never formats money or
 * percentages and neither may the model on its behalf — figures arrive as
 * typed params and are formatted below, from the device locale.
 */
const FORMATTED_NUMBER = /[$€£¥₹]|\d[\d,.]*\s*(%|USD|EUR|GBP)|\b\d{1,3}([,.]\d{3})+\b/

/** x-validation noMarkup: `<`, `>`, `[`, `](`, backtick, newline, or any emoji. */
const MARKUP = /[<>[\]`\n\r]|\p{Extended_Pictographic}/u

const PLACEHOLDER = /\{(\w+)\}/g

/** `en-US` and `en` share a base language; `en-US` and `de-DE` do not. */
export function baseLanguage(tag: string): string {
  return tag.toLowerCase().split(/[-_]/)[0] ?? ''
}

/** Every `{name}` in a string, deduplicated and in order of appearance. */
function placeholdersIn(text: string): string[] {
  return [...new Set([...text.matchAll(PLACEHOLDER)].map((m) => m[1]))]
}

const isMoney = (value: unknown): value is Money =>
  typeof value === 'object' &&
  value !== null &&
  typeof (value as Money).amount === 'number' &&
  typeof (value as Money).currency === 'string'

/**
 * Run the client-side half of x-validation. Returns the gate that refused, or
 * null when the object is safe to interpolate.
 *
 * `displayLocale` is the locale the user is actually reading in — a template
 * written in another base language is discarded rather than machine-translated
 * a second time, and the keyed fallback (which IS translated) renders instead.
 */
export function gateGeneratedText(
  text: GeneratedText,
  displayLocale: string,
): GateFailure | null {
  const bullets = text.bullets ?? []

  // lengthCaps
  if (
    typeof text.template !== 'string' ||
    text.template.length === 0 ||
    text.template.length > MAX_TEMPLATE ||
    bullets.length > MAX_BULLETS ||
    bullets.some((b) => typeof b !== 'string' || b.length === 0 || b.length > MAX_BULLET) ||
    Object.keys(text.params ?? {}).length > MAX_PARAMS
  ) {
    return 'lengthCaps'
  }

  const strings = [text.template, ...bullets]

  // noMarkup
  if (strings.some((s) => MARKUP.test(s))) return 'noMarkup'

  // noFormattedNumbers
  if (strings.some((s) => FORMATTED_NUMBER.test(s))) return 'noFormattedNumbers'

  // localeMatch
  if (
    typeof text.locale !== 'string' ||
    baseLanguage(text.locale) !== baseLanguage(displayLocale)
  ) {
    return 'localeMatch'
  }

  // placeholdersResolve
  const params = text.params ?? {}
  for (const s of strings) {
    for (const name of placeholdersIn(s)) {
      if (!(name in params)) return 'placeholdersResolve'
    }
  }

  return null
}

/**
 * One typed param as the user's locale would write it. The type is carried on
 * the wire precisely because a client cannot tell from the key name whether
 * `{amount}` is money or a merchant name.
 *
 * Money is rendered SIGNED here, unlike lib/display.ts `displayParams`, which
 * takes the magnitude. A keyed template was authored knowing its own sign
 * convention ("You spent {amount}"); a run-time template was not, so dropping
 * the sign off a negative net would turn a correct figure into a wrong one.
 */
function formatParam(param: unknown): string | null {
  if (typeof param !== 'object' || param === null) return null
  const { type, value } = param as { type?: unknown; value?: unknown }

  switch (type) {
    case 'money':
      return isMoney(value) ? formatMoney(value) : null
    case 'date':
      return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value) ? formatDate(value) : null
    case 'month':
      return typeof value === 'string' && /^\d{4}-\d{2}$/.test(value) ? formatMonth(value) : null
    case 'percent':
      return typeof value === 'number' && Number.isFinite(value) ? formatPercent(value, 0) : null
    case 'count':
      return typeof value === 'number' && Number.isInteger(value) ? formatNumber(value) : null
    case 'text':
      return typeof value === 'string' && value.length <= 60 ? value : null
    default:
      return null
  }
}

/** Substitute every `{name}`. A param that will not format aborts the whole string. */
function interpolate(template: string, params: Record<string, unknown>): string | null {
  let failed = false
  const out = template.replace(PLACEHOLDER, (whole, name: string) => {
    const formatted = formatParam(params[name])
    if (formatted === null) {
      failed = true
      return whole
    }
    return formatted
  })
  return failed ? null : out
}

export interface ResolvedGeneratedText {
  /** The interpolated template — one or two plain sentences. */
  text: string
  /** Interpolated `bullets`, empty when the object carried none. */
  bullets: string[]
}

/**
 * Resolve model-authored prose for display, or return null to mean "render the
 * copyKey". Null covers all four of the schema's fallback triggers: the field
 * was absent, a gate refused, the locale did not match, or a param would not
 * interpolate.
 *
 * Nothing here throws. A slot that cannot render prose renders keyed copy, and
 * that is a correct screen rather than a degraded one.
 */
export function resolveGeneratedText(
  text: GeneratedText | null | undefined,
  displayLocale: string,
  onRefusal?: (gate: GateFailure | 'interpolation') => void,
): ResolvedGeneratedText | null {
  if (!text) return null

  const gate = gateGeneratedText(text, displayLocale)
  if (gate) {
    onRefusal?.(gate)
    return null
  }

  const params = text.params ?? {}
  const resolved = interpolate(text.template, params)
  if (resolved === null) {
    onRefusal?.('interpolation')
    return null
  }

  const bullets: string[] = []
  for (const bullet of text.bullets ?? []) {
    const line = interpolate(bullet, params)
    // One unrenderable bullet discards the whole object: a step list missing
    // its third step is worse than the deterministic list it falls back to.
    if (line === null) {
      onRefusal?.('interpolation')
      return null
    }
    bullets.push(line)
  }

  return { text: resolved, bullets }
}
